import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router'

import { Role } from '@ankor-io/common/types/account'

import { useUserContext } from '@/providers/user/useUserContext'
import Loading from '@/ui-components/Loading'

interface ProtectedRouteProps {
  children: ReactNode
  /** Specify allowed roles for a route. This overrides minRole. */
  allowedRoles?: Role[]
  /** Specify the minimum role required to access a route. */
  minRole?: Role
  /** optional path to send users who are authenticated but not authorized (403 Forbidden) */
  forbiddenPath?: string
  /** optional loading indicator */
  fallback?: ReactNode
}

export function ProtectedRoute({
  children,
  allowedRoles,
  minRole,
  forbiddenPath = '/forbidden',
  fallback = <Loading />,
}: ProtectedRouteProps) {
  const { isAuthenticated } = useKindeAuth()
  const { isLoading, organizations, activeOrganizationId } = useUserContext()
  const navigate = useNavigate()

  // If we're not authenticated, kick off Kinde login
  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/sign-in')
    }
  }, [isAuthenticated, navigate])

  // waiting for auth to resolve
  if (isLoading) {
    return fallback
  }

  const activeOrg = activeOrganizationId ? organizations[activeOrganizationId] : null

  // If no active organization, redirect to unauthorized
  if (!activeOrg) {
    return (
      <Navigate
        to={forbiddenPath}
        replace
      />
    )
  }

  // Check if user has any of the allowed roles
  if (allowedRoles?.length && activeOrg.roles.some((userRole) => allowedRoles.includes(userRole))) {
    return <>{children}</>
  }

  if (minRole) {
    // Role is an enum that is ordered from highest to lowest
    // We can convert this enum into an array and use the index to compare the user's role with the minimum required role
    const roleHierarchy = Object.values(Role) as Role[]
    const userRoleIndex = roleHierarchy.indexOf(activeOrg.roles[0])
    const minRoleIndex = roleHierarchy.indexOf(minRole)

    if (userRoleIndex <= minRoleIndex) {
      return <>{children}</>
    }
  }

  // By default, redirect to 403 Forbidden
  return (
    <Navigate
      to={forbiddenPath}
      replace
    />
  )
}
